import { wvusLogger } from "../../helpers";

/**
 * Returns true if the current device supports Apple Pay and can make payments.
 */
const isAppleDevice = () => {
  if (
    window?.ApplePaySession &&
    ApplePaySession.supportsVersion(3) &&
    ApplePaySession.canMakePayments()
  ) {
    return true;
  } else {
    return false;
  }
};

/**
 * Fetches the Apple Pay capabilities for a given merchant.
 * @param merchantIdentifier The merchant identifier to check capabilities for.
 * @returns An object containing the capabilities and active card status.
 */
async function getApplePayCapabilities(merchantIdentifier: string) {
  const capabilities = await ApplePaySession.applePayCapabilities(merchantIdentifier);
  
  // The device supports Apple Pay (we got a response from the API)
  // but isCapable is only true if there's an active card
  const supportsApplePay = true;
  const isCapable = capabilities.paymentCredentialStatus === "paymentCredentialsAvailable";

  wvusLogger.log(
    `Apple Pay capabilities: paymentCredentialStatus=${capabilities.paymentCredentialStatus}`,
    wvusLogger.getLogLevelNames().INFO
  );

  return { capabilities, isCapable, supportsApplePay };
}

/**
 * Determines if the Apple Pay capabilities API should be used.
 * In development environments, we use a fallback method.
 * @returns True if the Apple Pay capabilities API should be used, false otherwise.
 */
function shouldUseApplePayCapabilities(): boolean {
  const viteEnv = typeof import.meta !== "undefined" && import.meta.env?.VITE_ENV;

  return !(viteEnv === "development");
}

/**
 * Checks if the current device is capable of Apple Pay with a valid card set up in Apple Wallet.
 * @returns An object indicating whether the device is capable of Apple Pay, supports Apple Pay, and has an active card.
 * If an error occurs, it will include an error message.
 */
const checkApplePayCapabilities = async (applePayMerchantId: string): Promise<{
  supportsApplePay: boolean;
  isCapable: boolean;
  error?: string;
}> => {
  if (!isAppleDevice()) {
    return {
      supportsApplePay: false,
      isCapable: false,
      error: "Device does not support Apple Pay"
    };
  }

  try {
    if (shouldUseApplePayCapabilities()) {
      const { capabilities, isCapable, supportsApplePay } = await getApplePayCapabilities(applePayMerchantId);
      wvusLogger.log(
        `Apple Pay capabilities: isCapable=${isCapable}, status=${capabilities.paymentCredentialStatus}`,
        wvusLogger.getLogLevelNames().INFO
      );
      return {
        isCapable,
        supportsApplePay
      };
    }

    // Fallback for environments unable to use applePayCapabilities API
    return {
      isCapable: true, // isAppleDevice already checked ApplePaySession.canMakePayments()
      supportsApplePay: true
    };
  } catch (capabilityError) {
    const errorMessage = capabilityError.message || String(capabilityError);
    wvusLogger.log(
      `Apple Pay capabilities API failed: ${errorMessage}`,
      wvusLogger.getLogLevelNames().ERROR
    );
    return {
      isCapable: false,
      supportsApplePay: true, // Device still supports Apple Pay despite the error
      error: `Error checking payment credentials: ${errorMessage}`
    };
  }
};

export { checkApplePayCapabilities };
